import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarBlank, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import SEO from "../components/SEO";
import { gbp } from "../components/admin/bookingFormat";
import { fmtSlot } from "../components/booking/SlotPicker";

const POLICY = {
  full: "You're cancelling 48 hours or more before your appointment, so you'll get a full refund.",
  half: "You're cancelling between 24 and 48 hours before your appointment, so you'll get a 50% refund.",
  none: "You're cancelling less than 24 hours before your appointment, so no refund is due under our policy.",
};

const Shell = ({ children }) => (
  <div data-testid="manage-booking-page" className="container-ccc py-16 max-w-2xl">
    <SEO title="Manage your booking" path="/manage-booking" />
    <div className="eyebrow">Your appointment</div>
    <h1 className="font-serif text-4xl sm:text-5xl mt-3">Manage your booking.</h1>
    <div className="mt-10">{children}</div>
  </div>
);

const Card = ({ tone = "default", children, testId }) => (
  <div data-testid={testId} className={`bg-white border rounded-md p-8 ${tone === "error" ? "border-red-200" : "border-bronze-light/40"}`}>{children}</div>
);

const Done = ({ amount, failed }) => (
  <Card testId="manage-booking-cancelled">
    <div className="flex items-center gap-2 text-bronze-dark"><CheckCircle size={20} weight="fill" /><b>Your appointment is cancelled</b></div>
    <p className="mt-4 text-sm text-ink-muted">
      {failed ? "Your refund is being processed by our team - you'll receive it shortly."
        : amount ? `A refund of ${gbp(amount)} is on its way to your card. It usually appears within 5-10 working days.`
          : "No refund was due under our cancellation policy."}
      {" "}We&apos;ve emailed you a confirmation.
    </p>
    <div className="mt-6"><Link to="/enquiry" className="btn-primary">Book a new time</Link></div>
  </Card>
);

export default function ManageBooking() {
  const [params] = useSearchParams();
  const b = params.get("b") || "";
  const t = params.get("t") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/booking/manage", { params: { b, t } }).then(({ data }) => setData(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [b, t]);

  const cancel = async () => {
    setBusy(true); setError("");
    try {
      const { data: r } = await api.post("/booking/manage/cancel", { b, t });
      setResult(r);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Shell><Done amount={result.refund_amount} failed={result.refund_failed} /></Shell>;
  if (error && !data) {
    return <Shell><Card tone="error" testId="manage-booking-error"><div className="flex items-center gap-2 text-red-700"><WarningCircle size={20} weight="fill" /><b>{error}</b></div>
      <p className="mt-4 text-sm text-ink-muted">Need help? Email <a className="text-bronze-dark" href="mailto:enquiries@camcpc.co.uk">enquiries@camcpc.co.uk</a> or call +44 (0) 1223 256653.</p></Card></Shell>;
  }
  if (!data) return <Shell><div className="text-ink-muted">Loading your booking…</div></Shell>;
  if (data.state === "cancelled" || data.state === "refunded") {
    return <Shell><Card testId="manage-booking-already-cancelled"><b>This appointment has already been cancelled.</b>
      {data.refund_amount > 0 && <p className="mt-3 text-sm text-ink-muted">A refund of {gbp(data.refund_amount)} was issued to your card.</p>}
      <div className="mt-6"><Link to="/enquiry" className="btn-primary">Book a new time</Link></div></Card></Shell>;
  }
  if (!data.quote || (!data.quote.calendly_active && !data.website)) {
    return <Shell><Card testId="manage-booking-unavailable"><b>This booking can&apos;t be changed online.</b>
      <p className="mt-3 text-sm text-ink-muted">Please email <a className="text-bronze-dark" href="mailto:enquiries@camcpc.co.uk">enquiries@camcpc.co.uk</a> or call +44 (0) 1223 256653.</p></Card></Shell>;
  }
  const q = data.quote;
  return (
    <Shell>
      <Card testId="manage-booking-summary">
        <div className="flex items-center gap-2 text-bronze-dark"><CalendarBlank size={18} weight="duotone" /><span className="eyebrow">Booked</span></div>
        <div data-testid="manage-booking-when" className="mt-2 font-serif text-2xl">{fmtSlot(data.start_time)}</div>
        {data.consultant && <div className="mt-1 text-sm text-ink-muted">with {data.consultant}</div>}
        <div className="mt-8 p-5 bg-cream-100 border border-bronze-light/40 rounded-md">
          <div className="text-xs uppercase tracking-[0.16em] text-ink-muted">If you cancel now</div>
          <div data-testid="manage-booking-refund" className="mt-2 font-serif text-3xl">{q.suggested ? `${gbp(q.suggested)} refund` : "No refund"}</div>
          <p data-testid="manage-booking-policy" className="mt-2 text-sm text-ink-muted">{POLICY[q.policy]} You paid {gbp(q.amount)}.</p>
        </div>
        {error && <div data-testid="manage-booking-action-error" className="mt-4 text-sm text-destructive">{error}</div>}
        <div className="mt-8 flex flex-wrap gap-3">
          {data.reschedule_url && (
            <a href={data.reschedule_url} data-testid="manage-booking-reschedule" className="btn-primary">Reschedule instead (keeps your payment)</a>
          )}
          {!confirming ? (
            <button type="button" onClick={() => setConfirming(true)} data-testid="manage-booking-cancel" className="btn-secondary">Cancel appointment</button>
          ) : (
            <>
              <button type="button" onClick={cancel} disabled={busy} data-testid="manage-booking-confirm-cancel"
                className="btn-primary bg-red-700 hover:bg-red-800 disabled:opacity-50">
                {busy ? "Cancelling…" : q.suggested ? `Yes, cancel and refund ${gbp(q.suggested)}` : "Yes, cancel (no refund)"}
              </button>
              <button type="button" onClick={() => setConfirming(false)} disabled={busy} data-testid="manage-booking-keep"
                className="btn-secondary">Keep my booking</button>
            </>
          )}
        </div>
      </Card>
    </Shell>
  );
}
